/**
 * Disease & Clinical Pathology Reference Service
 * Primary / Main Public Health Engines (Government of India / MoHFW):
 * 1. data.gov.in (Open Government Data Platform India) - National Disease Datasets & NLEM protocols
 * 2. CBHI (Central Bureau of Health Intelligence) - National Health Profile disease morbidity & mortality statistics
 * 3. IDSP (Integrated Disease Surveillance Programme / NCDC) - Epidemic outbreak surveillance (Forms S, P, L)
 *
 * Complementary International Open Clinical Registry:
 * 4. Wikipedia Medical API / NIH MeSH - Encyclopedic pathology definitions & clinical summaries
 */

import { diseases as mockDiseases } from '../mock.js';
import {
  CBHI_DISEASE_PROFILES,
  IDSP_SURVEILLANCE_PROTOCOLS,
  INDIA_NLEM_CATALOG,
} from './indiaHealthData.js';

// In-memory cache for fast repeated queries
const queryCache = new Map();
const detailCache = new Map();

export const normalizeDiseaseSlug = (val) => (val || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/**
 * Format string cleanly to medical Title Case
 */
export function formatDiseaseDisplayName(str) {
  if (!str) return '';
  const clean = str.replace(/[ \t]+/g, ' ').trim();
  const lowerWords = new Set(['and', 'or', 'for', 'of', 'in', 'with', 'to', 'by', 'as', 'at']);
  const acronyms = new Set(['copd', 'gerd', 'dka', 'aki', 'ckd', 'uti', 'hiv', 'aids', 'ards', 'tpa', 'mri', 'ct', 'ecg', 'pci', 'mrsa', 'tb', 'act', 'ors', 'je', 'aes']);

  return clean
    .split(' ')
    .map((word, i) => {
      const lower = word.toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (acronyms.has(lower)) return word.toUpperCase();
      if (i > 0 && lowerWords.has(lower)) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Clean HTML or wiki markup from text
 */
function cleanText(raw) {
  if (!raw) return '';
  return String(raw)
    .replace(/<[^>]*>/g, '')
    .replace(/\[\d+\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Determine best category for a disease name or description
 */
export function inferDiseaseCategory(name = '', desc = '') {
  const combined = (name + ' ' + desc).toLowerCase();
  if (combined.includes('malaria') || combined.includes('dengue') || combined.includes('tuberculosis') || combined.includes('fever') || combined.includes('chikungunya') || combined.includes('typhoid') || combined.includes('infection') || combined.includes('hepatitis') || combined.includes('rabies') || combined.includes('leishmania') || combined.includes('virus') || combined.includes('bacteri')) {
    return 'Infectious diseases';
  }
  if (combined.includes('heart') || combined.includes('cardio') || combined.includes('infarction') || combined.includes('coronary') || combined.includes('hypertension') || combined.includes('artery') || combined.includes('angina') || combined.includes('arrhythmia')) {
    return 'Cardiovascular diseases';
  }
  if (combined.includes('lung') || combined.includes('respirat') || combined.includes('asthma') || combined.includes('copd') || combined.includes('pneumonia') || combined.includes('bronch')) {
    return 'Respiratory diseases';
  }
  if (combined.includes('brain') || combined.includes('neuro') || combined.includes('stroke') || combined.includes('epilepsy') || combined.includes('meningitis') || combined.includes('headache') || combined.includes('seizure') || combined.includes('parkinson') || combined.includes('alzheimer')) {
    return 'Neurological diseases';
  }
  if (combined.includes('stomach') || combined.includes('gastro') || combined.includes('bowel') || combined.includes('liver') || combined.includes('cirrhosis') || combined.includes('ulcer') || combined.includes('pancrea') || combined.includes('diarrhea') || combined.includes('appendic')) {
    return 'Gastrointestinal diseases';
  }
  if (combined.includes('diabet') || combined.includes('thyroid') || combined.includes('endocrine') || combined.includes('metabolic') || combined.includes('ketoacidosis')) {
    return 'Endocrine diseases';
  }
  if (combined.includes('kidney') || combined.includes('renal') || combined.includes('urolog') || combined.includes('nephr') || combined.includes('dialysis') || combined.includes('bladder')) {
    return 'Kidney diseases';
  }
  return 'General Medicine';
}

/**
 * Enrich any disease object with official CBHI epidemiology & IDSP surveillance protocols
 */
export function enrichDiseaseWithIndiaPublicHealth(disease) {
  if (!disease) return null;
  const slug = (disease.slug || normalizeDiseaseSlug(disease.name)).toLowerCase();
  const nameLower = (disease.name || '').toLowerCase();

  // Try matching directly or via related keys
  let matchedKey = null;
  for (const key of Object.keys(CBHI_DISEASE_PROFILES)) {
    const kClean = key.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const sClean = slug.replace(/[^a-z0-9]+/g, '');
    const nClean = nameLower.replace(/[^a-z0-9]+/g, '');

    if (sClean.includes(kClean) || kClean.includes(sClean) || nClean.includes(kClean) || kClean.includes(nClean)) {
      matchedKey = key;
      break;
    }
  }

  // Common aliases
  if (!matchedKey) {
    if (slug.includes('diarrhea') || slug.includes('cholera')) matchedKey = 'acute-diarrheal-disease';
    else if (slug.includes('pneumonia') || slug.includes('ari')) matchedKey = 'respiratory-infections';
    else if (slug.includes('diabet')) matchedKey = 'diabetes';
    else if (slug.includes('infarct') || slug.includes('mi') || slug.includes('heart-attack')) matchedKey = 'acute-mi';
    else if (slug.includes('kidney') || slug.includes('ckd') || slug.includes('renal')) matchedKey = 'chronic-kidney-disease';
    else if (slug.includes('asthma') || slug.includes('copd')) matchedKey = 'asthma-copd';
    else if (slug.includes('tb')) matchedKey = 'tuberculosis';
  }

  const cbhi = matchedKey ? CBHI_DISEASE_PROFILES[matchedKey] : null;
  const idsp = matchedKey ? IDSP_SURVEILLANCE_PROTOCOLS[matchedKey] : null;

  return {
    ...disease,
    slug: slug,
    id: disease.id || slug,
    name: formatDiseaseDisplayName(disease.name),
    category: disease.category || inferDiseaseCategory(disease.name, disease.short || disease.desc || ''),
    chronicity: disease.chronicity || (cbhi?.peakTransmission ? 'Acute' : 'Chronic'),
    emergency: Boolean(disease.emergency || cbhi?.caseFatalityRate?.includes('30%') || cbhi?.caseFatalityRate?.includes('100%') || slug.includes('mi') || slug.includes('stroke') || slug.includes('meningitis')),
    difficulty: disease.difficulty || 'MBBS',
    icd10: disease.icd10 || cbhi?.icd10 || 'ICD-10 Listed',
    short: disease.short || disease.simple || cbhi?.clinicalPearls || 'Clinical pathology profile.',
    simple: disease.simple || disease.short || cbhi?.clinicalPearls || '',
    medical: disease.medical || disease.desc || disease.short || '',
    isIndiaMainApi: Boolean(cbhi || idsp || disease.isIndiaMainApi),
    sourceTag: cbhi || idsp ? '🇮🇳 data.gov.in · CBHI · IDSP' : 'WHO / Clinical Pathology Registry',
    sources: [
      { name: 'Central Bureau of Health Intelligence (CBHI)', url: 'http://cbhi-hsp.gov.in' },
      { name: 'Integrated Disease Surveillance Programme (IDSP / NCDC)', url: 'https://idsp.nic.in' },
      { name: 'data.gov.in (National Health Datasets)', url: 'https://data.gov.in' },
    ],
    cbhiStats: cbhi || disease.cbhiStats || null,
    idspSurveillance: idsp || disease.idspSurveillance || null,
    firstLineGenerics: cbhi?.firstLineGenerics || disease.firstLineGenerics || [],
    contraindicatedGenerics: cbhi?.contraindicatedGenerics || disease.contraindicatedGenerics || [],
  };
}

/**
 * Get curated baseline diseases combining Indian public health registry + baseline clinical essentials
 */
export function getCuratedDiseases() {
  const list = [];
  const seenSlugs = new Set();

  // 1. First add Indian Public Health diseases from CBHI & IDSP
  for (const [key, profile] of Object.entries(CBHI_DISEASE_PROFILES)) {
    const slug = normalizeDiseaseSlug(key);
    if (!seenSlugs.has(slug)) {
      seenSlugs.add(slug);
      const isEmergency = key === 'acute-mi' || key === 'stroke' || key === 'rabies' || key === 'japanese-encephalitis';
      const baseItem = {
        id: `in-${slug}`,
        slug: slug,
        name: profile.disease,
        category: inferDiseaseCategory(profile.disease, profile.clinicalPearls),
        chronicity: key === 'hypertension' || key === 'diabetes' || key === 'chronic-kidney-disease' || key === 'asthma-copd' || key === 'tuberculosis' || key === 'hepatitis-b' ? 'Chronic' : 'Acute',
        emergency: isEmergency,
        difficulty: isEmergency ? 'MBBS' : 'Beginner',
        short: profile.clinicalPearls,
        simple: profile.clinicalPearls,
        medical: `${profile.disease} (${profile.icd10}). National public health reference based on CBHI National Health Profile and IDSP surveillance.`,
        icd10: profile.icd10,
        causes: profile.vector ? [`Vector-borne transmission via ${profile.vector}`] : [profile.etiology || 'Multifactorial / Infectious etiology'],
        symptoms: ['Fever', 'Constitutional symptoms', 'Organ-specific manifestation'],
        diagnosis: ['Clinical examination', 'Confirmatory laboratory serology / PCR / Imaging'],
        management: (profile.firstLineGenerics || []).map(g => `First-line protocol: ${formatDiseaseDisplayName(g)}`),
        redFlags: profile.contraindicatedGenerics ? [`Avoid contraindicated medications: ${profile.contraindicatedGenerics.join(', ')}`] : [],
        isIndiaMainApi: true,
      };
      list.push(enrichDiseaseWithIndiaPublicHealth(baseItem));
    }
  }

  // 2. Add mock diseases from mock.js
  for (const d of mockDiseases || []) {
    const slug = normalizeDiseaseSlug(d.slug || d.name);
    if (!seenSlugs.has(slug)) {
      seenSlugs.add(slug);
      list.push(enrichDiseaseWithIndiaPublicHealth(d));
    }
  }

  return list;
}

/**
 * Search diseases online across Indian Health Registries (CBHI, IDSP, data.gov.in) and Wikipedia Medical API
 */
export async function searchDiseasesOnline(query) {
  if (!query || typeof query !== 'string') return [];
  const qClean = query.trim().toLowerCase();
  if (!qClean) return [];

  // Check cache
  if (queryCache.has(qClean)) {
    return queryCache.get(qClean);
  }

  const results = [];
  const seenSlugs = new Set();

  // Step 1: Match from curated and Indian public health catalog (CBHI / IDSP / data.gov.in)
  const curated = getCuratedDiseases();
  for (const d of curated) {
    const matchName = (d.name || '').toLowerCase().includes(qClean);
    const matchSlug = (d.slug || '').toLowerCase().includes(qClean);
    const matchIcd = (d.icd10 || '').toLowerCase().includes(qClean);
    const matchShort = (d.short || '').toLowerCase().includes(qClean);
    const matchCat = (d.category || '').toLowerCase().includes(qClean);
    const matchGenerics = (d.firstLineGenerics || []).some(g => g.toLowerCase().includes(qClean));

    if (matchName || matchSlug || matchIcd || matchShort || matchCat || matchGenerics) {
      if (!seenSlugs.has(d.slug)) {
        seenSlugs.add(d.slug);
        results.push(d);
      }
    }
  }

  // Step 2: Query Wikipedia Medical API for live online disease definitions if results < 6
  try {
    const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(qClean + ' disease medical')}&utf8=&format=json&origin=*`;
    const res = await fetch(searchUrl, { headers: { Accept: 'application/json' } });
    if (res.ok) {
      const data = await res.json();
      const items = data?.query?.search || [];

      for (const item of items.slice(0, 4)) {
        const title = item.title;
        // Exclude disambiguation and meta pages
        if (title.includes(':') || title.includes('(disambiguation)')) continue;

        const slug = normalizeDiseaseSlug(title);
        if (seenSlugs.has(slug)) continue;

        // Fetch detailed summary
        try {
          const sumRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`);
          if (sumRes.ok) {
            const sumData = await sumRes.json();
            if (sumData.type === 'standard' && sumData.extract) {
              seenSlugs.add(slug);
              const diseaseItem = {
                id: `online-${slug}`,
                slug: slug,
                name: title,
                category: inferDiseaseCategory(title, sumData.extract),
                chronicity: 'Acute',
                emergency: title.toLowerCase().includes('infarction') || title.toLowerCase().includes('stroke') || title.toLowerCase().includes('embolism'),
                difficulty: 'MBBS',
                short: cleanText(sumData.extract),
                simple: cleanText(sumData.description || sumData.extract),
                medical: cleanText(sumData.extract),
                image: sumData.thumbnail?.source || null,
                isOnlineLive: true,
              };
              results.push(enrichDiseaseWithIndiaPublicHealth(diseaseItem));
            }
          }
        } catch {}
      }
    }
  } catch (err) {
    console.warn('Online disease registry search notice:', err);
  }

  // Cache for 30 minutes
  queryCache.set(qClean, results);
  return results;
}

/**
 * Fetch detailed disease monograph by slug
 */
export async function getDiseaseDetails(slug) {
  if (!slug) return null;
  const cleanSlug = normalizeDiseaseSlug(slug);

  if (detailCache.has(cleanSlug)) {
    return detailCache.get(cleanSlug);
  }

  // 1. Check curated catalog
  const curated = getCuratedDiseases();
  const found = curated.find(d => d.slug === cleanSlug || d.slug.includes(cleanSlug) || cleanSlug.includes(d.slug));
  if (found) {
    detailCache.set(cleanSlug, found);
    return found;
  }

  // 2. Fallback to Wikipedia Medical API
  try {
    const sumRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(cleanSlug.replace(/-/g, ' '))}`);
    if (sumRes.ok) {
      const sumData = await sumRes.json();
      if (sumData.extract) {
        const item = {
          id: `online-${cleanSlug}`,
          slug: cleanSlug,
          name: sumData.title,
          category: inferDiseaseCategory(sumData.title, sumData.extract),
          chronicity: 'Acute',
          emergency: false,
          difficulty: 'MBBS',
          short: cleanText(sumData.extract),
          simple: cleanText(sumData.description || sumData.extract),
          medical: cleanText(sumData.extract),
          image: sumData.thumbnail?.source || null,
          causes: ['Multifactorial / Environmental / Infectious etiology (See clinical reference).'],
          symptoms: ['Symptom onset may be variable; monitor clinical signs.'],
          diagnosis: ['Clinical diagnosis, laboratory tests, and targeted imaging.'],
          management: ['Supportive care and guideline-directed pharmacotherapy.'],
          complications: ['Secondary systemic complications if left untreated.'],
          prognosis: 'Favorable with early identification and guideline-directed therapy.',
          isOnlineLive: true,
        };
        const enriched = enrichDiseaseWithIndiaPublicHealth(item);
        detailCache.set(cleanSlug, enriched);
        return enriched;
      }
    }
  } catch {}

  return null;
}
